import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db';
import { logout, type Me } from '../session';
import { disablePush, enablePush, pushState, type PushState } from '../push';

/**
 * Top-level settings for the whole hub.
 *
 * These are the things that are true of the app rather than of any one module —
 * a notification permission is granted to the *origin*, one subscription per
 * phone, and it carries pushes from groceries, agenda and todos alike. So the
 * control belongs here, above the modules, not inside the groceries Status
 * screen where it used to sit. Module-specific status (store health, aisle
 * order) stays in its module.
 */

const PUSH_EXPLANATION: Record<PushState, string> = {
  on: 'Aan — voor boodschappen, agenda en taken',
  off: 'Uit',
  denied:
    'Geblokkeerd in de instellingen van deze telefoon. Meldingen voor deze app moeten daar eerst weer aangezet worden.',
  unavailable:
    'Alleen beschikbaar als deze app op je beginscherm staat. Open hem daarvandaan en dit komt terug.',
  not_configured: 'Niet ingesteld op deze server',
};

export function SettingsScreen() {
  const me = useLiveQuery(async () => (await db.meta.get('me'))?.value as Me | undefined, []);

  /**
   * Notifications (API §9). One decision, one toggle — ARCHITECTURE §3.3 is
   * about not building the second setting screen, and this is the first.
   */
  const [push, setPush] = useState<PushState | null>(null);
  const [pushBusy, setPushBusy] = useState(false);
  useEffect(() => {
    void pushState().then(setPush);
  }, []);

  async function togglePush() {
    setPushBusy(true);
    try {
      setPush(await (push === 'on' ? disablePush() : enablePush()));
    } finally {
      setPushBusy(false);
    }
  }

  return (
    <>
      <header className="topbar">
        <Link className="back btn btn-quiet" to="/">
          ‹ Terug
        </Link>
        <h1>Instellingen</h1>
      </header>

      <main className="stack">
        {push !== null && push !== 'not_configured' && (
          <>
            <p className="section-label">Meldingen</p>
            <div className="card">
              <div className="row row-top">
                <span className="row-main">
                  <span className="row-name">Op deze telefoon</span>
                  {/* `wraps`: these are sentences telling somebody how to fix
                      something, and a reason cut off mid-word is not one. */}
                  <span className="row-sub wraps">{PUSH_EXPLANATION[push]}</span>
                </span>
                {(push === 'on' || push === 'off') && (
                  <button className="btn btn-quiet" disabled={pushBusy} onClick={() => void togglePush()}>
                    {push === 'on' ? 'Uitzetten' : 'Aanzetten'}
                  </button>
                )}
              </div>
            </div>
            <p className="row-sub">Zet meldingen aan op elk toestel waar je ze wilt.</p>
          </>
        )}

        {me?.user && <p className="row-sub">Ingelogd als {me.user.display_name}</p>}

        <button
          className="btn btn-danger"
          onClick={() => {
            // Everything local goes with it: a shared or lost phone must not
            // keep the household's data behind a signed-out screen.
            void logout().then(() => window.location.replace('/'));
          }}
        >
          Uitloggen
        </button>
      </main>
    </>
  );
}
